import React, { useState } from "react";
import { Plus, Trash2, Info, Sparkles, X } from "lucide-react";
import api from "../../utils/api";

/**
 * Fuzzy (text similarity) match rule. Each column pair carries its OWN
 * confidence threshold — a row pair only qualifies when EVERY pair meets
 * its own threshold. Numeric columns are auto-stringified during scoring;
 * the original cell values in the results view are left untouched.
 *
 * Runs LAST in the engine, on rows not matched by any prior rule.
 * Matches land in the Partial / Review tab.
 */
export default function FuzzyBlock({ rule, index, onChange }) {
  const cfg = rule.fuzzy_config || {
    column_pairs: [{ a_col: "", b_col: "", threshold: 80 }],
    threshold: 80,          // fallback default used when a pair has no threshold
    algo: "token_set",
  };
  const pairs = cfg.column_pairs || [];
  const [suggestOpen, setSuggestOpen] = useState(false);

  const setCfg = (patch) => onChange({ ...rule, fuzzy_config: { ...cfg, ...patch } });
  const setPair = (i, field, v) => {
    const next = [...pairs];
    next[i] = { ...next[i], [field]: v };
    setCfg({ column_pairs: next });
  };
  const addPair = () => setCfg({ column_pairs: [...pairs, { a_col: "", b_col: "", threshold: cfg.threshold ?? 80 }] });
  const removePair = (i) => setCfg({ column_pairs: pairs.filter((_, j) => j !== i) });

  return (
    <div data-testid={`fuzzy-block-${index}`}
      style={{ marginTop: "0.75rem", padding: "0.875rem", background: "#FEF3C7", borderRadius: "0.5rem", border: "1px dashed #F59E0B" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "0.5rem" }}>
        <h4 style={{ margin: 0, fontSize: "0.82rem", fontWeight: 600, color: "#92400E" }}>Fuzzy similarity match</h4>
        <p style={{ margin: 0, fontSize: "0.7rem", color: "#92400E" }}>
          Runs LAST on leftover unmatched rows. Matches land in Partial / Review.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "0.5rem", alignItems: "end", marginBottom: "0.75rem" }}>
        <div>
          <label style={{ fontSize: "0.7rem", color: "#92400E", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "0.2rem" }}>Default confidence (%)</label>
          <input
            data-testid={`fuzzy-threshold-${index}`}
            type="number" min="1" max="100" step="1"
            value={cfg.threshold ?? 80}
            onChange={e => setCfg({ threshold: Number(e.target.value) })}
            className="recon-input"
            style={{ background: "white" }}
          />
          <p style={{ margin: "0.2rem 0 0", fontSize: "0.65rem", color: "#92400E", opacity: 0.8 }}>Used when a pair leaves its own threshold blank.</p>
        </div>
        <div>
          <label style={{ fontSize: "0.7rem", color: "#92400E", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "0.2rem" }}>Algorithm</label>
          <select
            data-testid={`fuzzy-algo-${index}`}
            value={cfg.algo || "token_set"}
            onChange={e => setCfg({ algo: e.target.value })}
            className="recon-input"
            style={{ background: "white", width: "100%" }}
          >
            <option value="token_set">Token Set Ratio (order-agnostic, word-level)</option>
            <option value="ratio">Simple Ratio (whole-string)</option>
          </select>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.4rem", padding: "0.5rem 0.65rem", background: "rgba(255,255,255,0.6)", border: "1px solid rgba(146,64,14,0.2)", borderRadius: "0.375rem", marginBottom: "0.6rem" }}>
        <Info size={13} style={{ color: "#92400E", flexShrink: 0, marginTop: "2px" }} />
        <p style={{ margin: 0, fontSize: "0.7rem", color: "#92400E", lineHeight: 1.45 }}>
          A row pair qualifies only when <strong>every pair below meets its own threshold</strong>. Numeric columns are automatically stringified for comparison — the original numeric values stay untouched in your results view.
        </p>
      </div>

      <div>
        <label style={{ fontSize: "0.7rem", color: "#92400E", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "0.3rem" }}>Column pairs</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px auto", gap: "0.4rem", alignItems: "center", padding: "0 0.1rem 0.2rem", fontSize: "0.62rem", color: "#92400E", opacity: 0.85, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          <span>File A column</span>
          <span>File B column</span>
          <span>Min %</span>
          <span></span>
        </div>
        {pairs.map((p, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px auto", gap: "0.4rem", marginBottom: "0.3rem", alignItems: "center" }}>
            <input
              data-testid={`fuzzy-a-col-${index}-${i}`}
              value={p.a_col || ""}
              onChange={e => setPair(i, "a_col", e.target.value)}
              placeholder="e.g. Narration"
              className="recon-input"
              style={{ background: "white" }}
            />
            <input
              data-testid={`fuzzy-b-col-${index}-${i}`}
              value={p.b_col || ""}
              onChange={e => setPair(i, "b_col", e.target.value)}
              placeholder="e.g. Description"
              className="recon-input"
              style={{ background: "white" }}
            />
            <input
              data-testid={`fuzzy-pair-threshold-${index}-${i}`}
              type="number" min="1" max="100" step="1"
              value={p.threshold ?? ""}
              onChange={e => setPair(i, "threshold", e.target.value === "" ? null : Number(e.target.value))}
              placeholder={String(cfg.threshold ?? 80)}
              className="recon-input"
              style={{ background: "white", textAlign: "right" }}
              title={`Per-pair confidence threshold. Leave blank to use default (${cfg.threshold ?? 80}%).`}
            />
            <button onClick={() => removePair(i)}
              disabled={pairs.length === 1}
              style={{ background: "none", border: "none", cursor: pairs.length === 1 ? "not-allowed" : "pointer", color: "#B45309", opacity: pairs.length === 1 ? 0.4 : 1 }}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          data-testid={`fuzzy-add-pair-${index}`}
          onClick={addPair}
          className="btn-secondary"
          style={{ fontSize: "0.72rem", padding: "0.25rem 0.55rem", background: "white", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
        >
          <Plus size={11} /> Add pair
        </button>
        <button
          data-testid={`fuzzy-suggest-btn-${index}`}
          onClick={() => setSuggestOpen(true)}
          disabled={!pairs.some(p => p.a_col && p.b_col)}
          style={{ marginLeft: "0.4rem", fontSize: "0.72rem", padding: "0.25rem 0.55rem", background: "white", color: "#92400E", border: "1px solid #F59E0B", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
          title="Sample two uploaded files and suggest thresholds per pair"
        >
          <Sparkles size={11} /> Suggest thresholds
        </button>
      </div>
      {suggestOpen && (
        <SuggestModal
          index={index}
          pairs={pairs.filter(p => p.a_col && p.b_col)}
          algo={cfg.algo || "token_set"}
          onClose={() => setSuggestOpen(false)}
          onApply={(perPair) => {
            // Apply suggestions by matching a_col+b_col
            const next = pairs.map(p => {
              const m = perPair.find(x => x.a_col === p.a_col && x.b_col === p.b_col);
              return m && m.suggested_threshold != null ? { ...p, threshold: m.suggested_threshold } : p;
            });
            setCfg({ column_pairs: next });
            setSuggestOpen(false);
          }}
        />
      )}
    </div>
  );
}


function SuggestModal({ index, pairs, algo, onClose, onApply }) {
  const [files, setFiles] = useState([]);
  const [fileA, setFileA] = useState("");
  const [fileB, setFileB] = useState("");
  const [sampleSize, setSampleSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    api.get("/reconciliation/files")
      .then(r => setFiles(r.data || []))
      .catch(() => setFiles([]));
  }, []);

  const run = async () => {
    if (!fileA || !fileB) { setErr("Pick a sample file for both sides"); return; }
    setErr(""); setLoading(true);
    try {
      const r = await api.post("/rule-sets/fuzzy-suggest", {
        file_a_id: fileA, file_b_id: fileB,
        column_pairs: pairs.map(p => ({ a_col: p.a_col, b_col: p.b_col })),
        algo, sample_size: sampleSize,
      });
      setResult(r.data);
    } catch (x) {
      setErr(x.response?.data?.detail || x.message || "Suggest failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={onClose} />
      <div data-testid={`fuzzy-suggest-modal-${index}`}
        style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "640px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.9rem" }}>
          <div>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0 }}>Suggest thresholds</h3>
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.78rem", color: "var(--text-muted)" }}>Pick two uploaded files to sample from. We score all-vs-all and recommend a threshold per pair at the 65th percentile of "best" matches.</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 120px", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <div>
            <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: "0.2rem" }}>Sample from File A</label>
            <select className="recon-input" value={fileA} onChange={e => setFileA(e.target.value)} data-testid={`fuzzy-suggest-file-a-${index}`}>
              <option value="">— pick —</option>
              {files.map(f => <option key={f.file_id || f.id} value={f.file_id || f.id}>{f.filename}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: "0.2rem" }}>Sample from File B</label>
            <select className="recon-input" value={fileB} onChange={e => setFileB(e.target.value)} data-testid={`fuzzy-suggest-file-b-${index}`}>
              <option value="">— pick —</option>
              {files.map(f => <option key={f.file_id || f.id} value={f.file_id || f.id}>{f.filename}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: "0.2rem" }}>Sample size</label>
            <input type="number" min="5" max="200" value={sampleSize} onChange={e => setSampleSize(Number(e.target.value))} className="recon-input" />
          </div>
        </div>

        <button data-testid={`fuzzy-suggest-run-${index}`} onClick={run} disabled={loading}
          className="btn-primary" style={{ fontSize: "0.82rem", padding: "0.45rem 1rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
          <Sparkles size={12} /> {loading ? "Sampling…" : "Run sampling"}
        </button>

        {err && <p style={{ color: "#B91C1C", fontSize: "0.78rem", margin: "0.6rem 0 0" }}>{err}</p>}

        {result?.per_pair?.length > 0 && (
          <div style={{ marginTop: "1rem", maxHeight: 260, overflowY: "auto", border: "1px solid var(--border-default)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
              <thead>
                <tr>
                  <th className="table-header" style={{ textAlign: "left" }}>Pair</th>
                  <th className="table-header" style={{ textAlign: "right" }}>Samples</th>
                  <th className="table-header" style={{ textAlign: "right" }}>p50</th>
                  <th className="table-header" style={{ textAlign: "right" }}>p65</th>
                  <th className="table-header" style={{ textAlign: "right" }}>p90</th>
                  <th className="table-header" style={{ textAlign: "right" }}>Suggested</th>
                </tr>
              </thead>
              <tbody>
                {result.per_pair.map((p, i) => (
                  <tr key={i} className="table-row">
                    <td className="table-cell" style={{ fontFamily: "JetBrains Mono, monospace" }}>{p.a_col}↔{p.b_col}</td>
                    <td className="table-cell" style={{ textAlign: "right" }}>{p.samples}</td>
                    <td className="table-cell" style={{ textAlign: "right" }}>{p.p50}</td>
                    <td className="table-cell" style={{ textAlign: "right" }}>{p.p65}</td>
                    <td className="table-cell" style={{ textAlign: "right" }}>{p.p90}</td>
                    <td className="table-cell" style={{ textAlign: "right", fontWeight: 700, color: "var(--sage)" }}>
                      {p.suggested_threshold ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {result?.per_pair?.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem", gap: "0.4rem" }}>
            <button onClick={onClose} className="btn-secondary" style={{ fontSize: "0.82rem" }}>Cancel</button>
            <button data-testid={`fuzzy-suggest-apply-${index}`} onClick={() => onApply(result.per_pair)} className="btn-primary" style={{ fontSize: "0.82rem" }}>
              Apply suggestions
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
